import { useEffect, useState } from "react";
import { Link, useRouter } from "@tanstack/react-router";
import { Menu, X, ShieldCheck, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import type { Session } from "@supabase/supabase-js";
import { PeopelsLogo } from "@/components/brand/PeopelsLogo";

export function Header() {
  const router = useRouter();
  const [session, setSession] = useState<Session | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  // Close mobile menu on Escape, lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open]);

  const signOut = async () => {
    await supabase.auth.signOut();
    router.navigate({ to: "/" });
  };

  const nav = [
    { to: "/browse", label: "Find Helpers" },
    { to: "/post-job", label: "Post a Job" },
    { to: "/become-helper", label: "Become a Helper" },
    { to: "/how-it-works", label: "How it works" },
  ] as const;

  return (
    <>
      {/* Skip to content — keyboard a11y */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[60] focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground focus:shadow-lg"
      >
        Skip to main content
      </a>

      <header className="sticky top-0 z-40 w-full border-b border-border/60 bg-background/80 backdrop-blur-md">
        {/* Trust bar — subtle marketplace credibility above primary nav */}
        <div className="hidden border-b border-border/40 bg-surface-muted/50 md:block">
          <div className="mx-auto flex h-7 max-w-7xl items-center justify-between gap-4 px-4 text-xs text-muted-foreground sm:px-6 lg:px-8">
            <p className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-primary" aria-hidden="true" /> ID-verified Helpers, secure escrow payments, London-based support.</p>
            <p>Card authorized at booking, funds released when the job is done.</p>
          </div>
        </div>

        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex shrink-0 items-center" aria-label="Peopels home">
            <PeopelsLogo variant="horizontal" height={32} />
          </Link>

          <nav className="hidden items-center gap-1 md:flex" aria-label="Primary">
            {nav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                activeProps={{ className: "text-foreground" }}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="hidden items-center gap-2 md:flex">
            {session ? (
              <>
                <Button asChild variant="ghost" size="icon" aria-label="Notifications">
                  <Link to="/notifications"><Bell className="h-4 w-4" /></Link>
                </Button>
                <Button asChild variant="ghost" size="sm"><Link to="/dashboard">Dashboard</Link></Button>
                <Button onClick={signOut} variant="outline" size="sm">Sign out</Button>
              </>
            ) : (
              <>
                <Button asChild variant="ghost" size="sm"><Link to="/auth">Sign in</Link></Button>
                <Button asChild size="sm" className="shadow-sm"><Link to="/auth" search={{ mode: "signup" }}>Get started</Link></Button>
              </>
            )}
          </div>

          <button
            type="button"
            className="grid h-11 w-11 place-items-center rounded-md text-foreground hover:bg-accent md:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-nav"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {open && (
          <div id="mobile-nav" className="border-t border-border bg-background md:hidden">
            <nav className="space-y-1 px-4 py-3" aria-label="Mobile">
              {nav.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className="block rounded-md px-3 py-3 text-base font-medium hover:bg-accent"
                >
                  {n.label}
                </Link>
              ))}
              <div className="flex gap-2 pt-2">
                {session ? (
                  <>
                    <Button asChild variant="outline" className="h-11 flex-1" onClick={() => setOpen(false)}><Link to="/dashboard">Dashboard</Link></Button>
                    <Button onClick={() => { setOpen(false); void signOut(); }} variant="ghost" className="h-11 flex-1">Sign out</Button>
                  </>
                ) : (
                  <>
                    <Button asChild variant="outline" className="h-11 flex-1" onClick={() => setOpen(false)}><Link to="/auth">Sign in</Link></Button>
                    <Button asChild className="h-11 flex-1" onClick={() => setOpen(false)}><Link to="/auth" search={{ mode: "signup" }}>Get started</Link></Button>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
